/**
 * MainLayout Component
 * Primary layout for authenticated pages with sidebar and header.
 */

import { useEffect, useState, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuthStore, useUser } from '@store/authStore';
import { env } from '@config/env';
import { DEV_MOCK_USERS } from './config/navigation';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { FloatingChatbotWidget } from '@modules/chatbot/components';
import { MemoPopupOverlay } from '@modules/memo/daie/components/MemoPopupOverlay';
import { notificationsApi } from '@modules/memo/services/notificationsService';
import type { Notification } from '@modules/memo/types';
import styles from './MainLayout.module.css';

/**
 * Main application layout with sidebar navigation and header.
 * Used for all authenticated/protected routes.
 */
export const MainLayout = () => {
  const user = useUser();
  const location = useLocation();
  const setAuth = useAuthStore((state) => state.setAuth);
  const impersonation = useAuthStore((s) => s.impersonation);
  const stopImpersonation = useAuthStore((s) => s.stopImpersonation);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 1024;
  });

  const [popupNotification, setPopupNotification] = useState<Notification | null>(null);
  const [unreadQueue, setUnreadQueue] = useState<Notification[]>([]);

  const showNextUnread = useCallback(async () => {
    const { data } = await notificationsApi.getNotifications({
      currentPage: 1,
      itemsPerPage: 20,
    });
    const unread = data.filter((n) => n.status === 'unread');
    if (unread.length > 0) {
      setPopupNotification(unread[0]);
      setUnreadQueue(unread.slice(1));
    } else {
      setPopupNotification(null);
      setUnreadQueue([]);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'daie') return;
    showNextUnread();
  }, [user?.role, location.pathname, showNextUnread]);

  const handlePopupClose = useCallback(() => {
    if (popupNotification?.memoId) {
      notificationsApi.markAsRead(popupNotification.memoId);
      window.dispatchEvent(new CustomEvent('notification-read'));
    }
    if (unreadQueue.length > 0) {
      setPopupNotification(unreadQueue[0]);
      setUnreadQueue((q) => q.slice(1));
    } else {
      setPopupNotification(null);
    }
  }, [popupNotification, unreadQueue]);

  // In development mode, auto-set a default user if none exists
  // But only if user didn't explicitly log out
  useEffect(() => {
    const authState = useAuthStore.getState();
    if (
      env.MODE === 'development' && 
      !user && 
      !authState.wasExplicitlyLoggedOut
    ) {
      // Default to admin user for development
      setAuth(DEV_MOCK_USERS.admin, 'dev-token');
    }
  }, [user, setAuth]);

  return (
    <div className={styles.layout}>
      {/* Sidebar Navigation */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className={styles.overlay}
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main Content Area */}
      <div className={`${styles.main} ${!isSidebarOpen ? styles.mainSidebarClosed : ''}`}>
        {/* Header */}
        <Header
          onToggleMobileMenu={() => setIsSidebarOpen(!isSidebarOpen)}
          isMobileMenuOpen={isSidebarOpen}
        />

        {impersonation && (
          <div className={styles.impersonationBanner} role="status" aria-live="polite">
            <div className={styles.impersonationBannerText}>
              Admin is viewing as <strong>{impersonation.targetUser?.name}</strong> (read-only)
            </div>
            <button
              type="button"
              className={styles.impersonationExitButton}
              onClick={stopImpersonation}
              title="Exit read-only view"
            >
              Exit
            </button>
          </div>
        )}

        {/* Page Content */}
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>

      {/* Floating Chatbot Widget */}
      <FloatingChatbotWidget />

      {/* System Popup Notification - auto popup for unread memos */}
      {popupNotification && (
        <MemoPopupOverlay
          notification={popupNotification}
          onClose={handlePopupClose}
        />
      )}
    </div>
  );
};
